import { IPromise, IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { IInsightboard, IMarketplaceInsightboardTemplate } from "../models/models";
import { IAssigneeIdMap, IAssignee } from "@gtmhub/assignees/models";
import { IGtmhubRootScopeService, ICollection } from "@gtmhub/models";
import { InsightboardService } from "@gtmhub/insightboards";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { Paginator } from "@gtmhub/teams";
import { IRole, RoleService } from "@gtmhub/roles";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { createFilter, GridFilterDefinitions, hasFilterValue, IGridFilter } from "@gtmhub/shared/components/grid-filtering-bar";
import {
  defaultOperatorFactories,
  RestLayerFilterFactory,
  toRestLayerRequest,
  IGridFiltersChange,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IRestLayerRequest } from "@gtmhub/core/rest-layer";
import { generateInsightboardsFilters, insightboardsFilterDefinitions } from "../filter-utils";
import { IDropdownButtonMenuItem } from "@gtmhub/shared/components";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { IStateInit } from "@gtmhub/core/routing";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { InsightTemplateSuggestionsService } from "../services/insight-template-suggestions.service";
import { filtersOverrideForTagTitle, filterFieldNames } from "@gtmhub/goals/filter-utils";
import { IdMap, pushOrReplace } from "@gtmhub/util";
import { IApiPermission } from "@gtmhub/sessions";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { IRecommendationArgs, recommendation, Flavor, AnalyticsService } from "@gtmhub/core/analytics";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

interface IInsightboardsRedux {
  assigneesIdMap: IAssigneeIdMap;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

export interface IInsightboardsScope extends IScope, IExtendedRootScopeService, IInsightboardsRedux {
  indicators: {
    loading: boolean;
    updating: boolean;
    deleting: boolean;
    cloning: boolean;
  };
  insightboards: ICollection<IInsightboard>;
  rolesIdMap: IdMap<IRole>;
  paginator: Paginator;
  selectNewInsightboardButtonDdItems: IDropdownButtonMenuItem[];
  featuredInsightboards: IMarketplaceInsightboardTemplate[];
  isFeaturedInsightboardsRequestFails: boolean;
  filters: IGridFilter[];
  filterDefinitions: GridFilterDefinitions;

  confirmDelete(insightboard: IInsightboard, index): void;
  filterByTag(tagTitle: string): void;
  openPermissionsDetails(insightboard: IInsightboard): void;
  onPageChange(page: number): void;
  filtersChange(change: IGridFiltersChange): void;
  anyFiltersApplied(): boolean;
  getTeamByRole(permission: IApiPermission): IAssignee | undefined;

  favoritesManager: FavoritesManager;
  getNavItem(insightboard: IInsightboard): INavItem;
  insightboardIsFavorite(insightboard: IInsightboard): boolean;
  isNewExperienceEnabled(): boolean;
  trackRecommendationHelpfulness(args: IRecommendationArgs): void;
}

const bindStateToScope = (state: IAssigneesStoreState): IInsightboardsRedux => ({
  assigneesIdMap: state.assignees.map,
  reduxItemsFetched: state.assignees.isFetched,
  reduxItemsError: state.assignees.error,
});

const screenPath = ["insightboards"];

export class InsightboardsCtrl implements IStateInit {
  private deleteModal: IModalServiceInstance;
  private permissionsModal: IModalServiceInstance;
  private favoritesManager: FavoritesManager;
  private strings = {
    loading: "loading",
    updating: "updating",
    deleting: "deleting",
    cloning: "cloning",
    insightboardFields: "id,name,tags,access,tag,currentUserAllowedActions",
    modifyPermissions: "modifyPermissions",
    read: "read",
    update: "update",
    delete: "delete",
    menu: "menu",
  };

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$uibModal",
    "$q",
    "InsightboardService",
    "RoleService",
    "UIErrorHandlingService",
    "AssigneeActions",
    "$ngRedux",
    "InsightTemplateSuggestionsService",
    "FeatureToggleV2Service",
    "FavoritesManagerFactory",
    "AccountService",
    "AnalyticsService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IInsightboardsScope,
    private $stateService: StateService,
    private $uibModal: IModalService,
    private $q: IQService,
    private insightboardService: InsightboardService,
    private roleService: RoleService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux,
    private insightTemplateSuggestionsService: InsightTemplateSuggestionsService,
    private featureToggleV2Service: FeatureToggleV2Service,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private analyticsService: AnalyticsService
  ) {
    this.favoritesManager = this.favoritesManagerFactory(["dashboard"]);
  }

  private showNewExperience: boolean;
  private checkingEnabledFeatures: boolean;

  stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.$on("insightboardsChanged", this.onInsightboardsChanged);
    this.$scope.$on("insightboardEdited", this.onInsightboardEdited);

    this.$scope.confirmDelete = this.confirmDelete;
    this.$scope.filterByTag = this.filterByTag;
    this.$scope.openPermissionsDetails = this.openPermissionsDetails;
    this.$scope.trackRecommendationHelpfulness = (args: IRecommendationArgs) => this.trackRecommendationHelpfulness(args);

    this.$scope.indicators = {
      loading: false,
      updating: false,
      cloning: false,
      deleting: false,
    };

    this.$rootScope.noNavigation = false;

    this.$scope.selectNewInsightboardButtonDdItems = this.generateSelectNewInsightboardButtonDropdownItems();

    this.setDefaultPaginator();
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.anyFiltersApplied = this.anyFiltersApplied;
    this.$scope.getTeamByRole = this.getTeamByRole;

    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.insightboardIsFavorite = this.insightboardIsFavorite;
    this.$scope.isNewExperienceEnabled = this.isNewExperienceEnabled;

    this.$scope.indicators.loading = true;

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => this.initializeData(),
        () => this.handleIndicatorError(this.$scope.reduxItemsError, this.strings.loading)
      );
  };

  private initializeData = (): IPromise<void> => {
    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;
    return this.$q
      .all({
        roles: this.roleService.getRoles({ includeTeamRoles: true }),
        isTagsModuleEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleTags),
        newExperienceEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.NewExperience),
        newExperienceToggleEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
      })
      .then(
        ({ roles, isTagsModuleEnabled, newExperienceEnabled, newExperienceToggleEnabled }) => {
          this.initGridFilteringBar(isTagsModuleEnabled);
          this.$scope.rolesIdMap = {};
          this.$scope.rolesIdMap = roles.items.reduce((rolesMap, currentRole) => {
            rolesMap[currentRole.id] = currentRole;
            return rolesMap;
          }, this.$scope.rolesIdMap);
          const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
          this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
          this.checkingEnabledFeatures = false;
          if (this.showNewExperience) {
            this.favoritesManager.initFavorites();
          }
        },
        (err) => {
          this.checkingEnabledFeatures = false;
          this.handleIndicatorError(err, this.strings.loading);
        }
      );
  };

  private initGridFilteringBar = (isTagsModuleEnabled: boolean) => {
    this.$scope.filterDefinitions = insightboardsFilterDefinitions();
    let persistedFilters = getPersistedScreenSettings(screenPath, "filters");

    if (!isTagsModuleEnabled) {
      delete this.$scope.filterDefinitions["tags.title"];
      persistedFilters = persistedFilters?.filter((x) => x.fieldName !== "tags.title");
    }

    if (persistedFilters?.length) {
      this.$scope.filters = persistedFilters;
    } else {
      this.$scope.filters = generateInsightboardsFilters(this.$scope.filterDefinitions, isTagsModuleEnabled);
    }
    this.$scope.filtersChange = this.filtersChange;
  };

  private filtersChange = (change: IGridFiltersChange) => {
    if (!change.initial) {
      setPersistedScreenSettings(screenPath, "filters", this.$scope.filters);
    }

    this.$scope.paginator.currentPage = 1;
    this.loadInsightboards();
  };

  private onPageChange = (page: number): void => {
    this.$scope.paginator.currentPage = page;
    this.loadInsightboards();
  };

  private anyFiltersApplied = (): boolean => this.$scope.filters && this.$scope.filters.some((f) => hasFilterValue(f));

  private setDefaultPaginator = () => {
    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 20,
      paginatorLimit: 5,
    };
  };

  private loadInsightboards = (): void => {
    this.$scope.indicators.updating = true;
    const request = this.createInsightboardsRequestV2();

    this.insightboardService.getInsightboardsV2(request).then(
      (boards) =>
        this.handleGetInsigthboardsResponse(boards).then(() => {
          this.$scope.indicators.loading = false;
          this.$scope.indicators.updating = false;
        }),
      (err) => this.handleIndicatorError(err, this.$scope.indicators.loading ? this.strings.loading : this.strings.updating, true)
    );
  };

  private handleGetInsigthboardsResponse = (boards: ICollection<IInsightboard>): IPromise<unknown> => {
    this.$scope.insightboards = { items: this.processPermissions(boards.items), totalCount: boards.totalCount };
    this.$scope.paginator.totalPages = Math.ceil(this.$scope.insightboards.totalCount / this.$scope.paginator.itemsPerPage);

    if (!boards.items.length) {
      return this.insightboardService.getFeaturedInsightboards().then(
        (featuredInsightboards) => {
          this.$scope.featuredInsightboards = featuredInsightboards.items;
        },
        () => {
          /*
            Allow users to use top-nav-bar actions if marketplace service is down
            or if the request failed for some reason
          */
          this.$scope.isFeaturedInsightboardsRequestFails = true;
        }
      );
    }

    return this.$q.resolve();
  };

  private createInsightboardsRequestV2 = (): IRestLayerRequest => {
    // We don't set value for dateModified unless the insightboard is updated,
    // that's why when we don't have dateModified we use the dateCreated value as dateModified value
    const filtersOverride: Record<string, RestLayerFilterFactory> = {
      dateModified: (filter) => {
        const defaultFactory = defaultOperatorFactories[filter.operator];
        return {
          $or: [
            { $and: [{ dateModified: { $exists: true } }, defaultFactory({ ...filter, fieldName: "dateModified" })] },
            { $and: [{ dateModified: { $exists: false } }, defaultFactory({ ...filter, fieldName: "dateCreated" })] },
          ],
        };
      },
      ...filtersOverrideForTagTitle(),
    };

    const request = toRestLayerRequest({ filters: this.$scope.filters, filtersOverride });
    request.skip = (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage;
    request.limit = this.$scope.paginator.itemsPerPage;
    request.fields = this.strings.insightboardFields;

    return request;
  };

  private processPermissions = (boards: IInsightboard[]): IInsightboard[] => {
    boards = boards.map((board: IInsightboard): IInsightboard => {
      board.allowedActions = {};

      board.allowedActions = board.currentUserAllowedActions.reduce(
        (
          actionsMap: { [action: string]: boolean },
          currentAction: string
        ): {
          [action: string]: boolean;
        } => {
          actionsMap[currentAction] = true;

          return actionsMap;
        },
        board.allowedActions
      );

      board.allowedActions[this.strings.menu] =
        (board.allowedActions[this.strings.read] && this.$rootScope.hasPermission("ManageData")) ||
        board.allowedActions[this.strings.update] ||
        board.allowedActions[this.strings.delete] ||
        board.allowedActions[this.strings.modifyPermissions]
          ? true
          : false;

      return board;
    });

    return boards;
  };

  private onInsightboardsChanged = (): void => {
    this.loadInsightboards();
  };

  private onInsightboardEdited = (): void => {
    this.loadInsightboards();
  };

  private confirmDelete = (insightboard: IInsightboard) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.deleteModal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete.jade"),
      windowClass: "medium",
      controller: [
        "$scope",
        function ($scope) {
          $scope.insightboard = insightboard;
          $scope.indicators = ctrl.$scope.indicators;

          $scope.confirm = () => {
            ctrl.delete(insightboard);
          };

          $scope.close = () => {
            ctrl.closeModal();
          };
        },
      ],
    });
  };

  private closeModal = (): void => {
    this.deleteModal.close();
  };

  private delete = (insightboard: IInsightboard) => {
    this.$scope.indicators.deleting = true;
    insightboard.deleting = true;

    this.insightboardService.deleteInsightboard(insightboard.id).then(
      () => {
        this.$scope.indicators.deleting = false;

        if (this.$scope.paginator.currentPage > 1 && this.$scope.insightboards.items.length === 1) {
          this.$scope.paginator.currentPage--;
        }
        // Remove the insight template suggestion settings if they are present
        this.insightTemplateSuggestionsService.removeSettings(insightboard.id);
        this.onInsightboardsChanged();
        this.closeModal();
      },
      (err) => {
        insightboard.deleting = false;
        this.handleIndicatorError(err, this.strings.deleting, true);
      }
    );
  };

  private filterByTag = (tagTitle: string): void => {
    let tagsFilter = angular.copy(this.$scope.filters.find((filter) => filter.fieldName === filterFieldNames.tags));

    if (tagsFilter && Array.isArray(tagsFilter.value)) {
      const isTagInFilters = tagsFilter.value.includes(tagTitle);

      if (isTagInFilters) {
        tagsFilter.value = tagsFilter.value.filter((name) => name !== tagTitle);
      } else {
        tagsFilter.value.push(tagTitle);
      }
    } else {
      tagsFilter = createFilter(this.$scope.filterDefinitions[filterFieldNames.tags]);
      tagsFilter.value = [tagTitle];
    }

    this.$scope.filters = pushOrReplace(this.$scope.filters, tagsFilter, (filter) => filter.fieldName === filterFieldNames.tags);
  };

  private openPermissionsDetails = (insightboard: IInsightboard): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.permissionsModal = this.$uibModal.open({
      templateUrl: require("../../shared/views/permissions-details.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.item = insightboard;
          $scope.canModify = insightboard.currentUserAllowedActions.indexOf(ctrl.strings.modifyPermissions) !== -1 ? true : false;

          $scope.assigneesIdMap = ctrl.$scope.assigneesIdMap;
          $scope.rolesIdMap = ctrl.$scope.rolesIdMap;

          $scope.editPermissions = () => {
            ctrl.permissionsModal.close();
            ctrl.$stateService.go("._edit.edit", { editDashboardId: insightboard.id });
          };

          $scope.close = () => {
            ctrl.permissionsModal.close();
          };

          $scope.getTeamByRole = ctrl.getTeamByRole;
        },
      ],
    });
  };

  private generateSelectNewInsightboardButtonDropdownItems = (): IDropdownButtonMenuItem[] => {
    return [
      {
        action: { href: "/marketplace/insightboards", target: "_blank" },
        textKey: "add_template",
        descriptionKey: "get_free_ready_to_use_insightboards_from_marketplace",
        imageSrc: require("wwwroot/img/icons/marketplace.svg"),
        externalLink: true,
        e2eData: "installTemplate",
        ghTrackEventName: "Menu Item Clicked",
        ghTrackMeta: "{menu_item_name: 'add_insightboard'}",
      },
      {
        action: { state: "._form.create" },
        textKey: "create_insightboard",
        descriptionKey: "build_your_custom_insightboard",
        imageSrc: require("wwwroot/img/icons/insightboard.svg"),
        e2eData: "createInsightboard",
        ghTrackEventName: "Menu Item Clicked",
        ghTrackMeta: "{menu_item_name: 'create_insightboard'}",
      },
    ];
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private getTeamByRole = (permission: IApiPermission): IAssignee | undefined => {
    const role: IRole = this.$scope.rolesIdMap[permission.principalId];
    return this.$scope.assigneesIdMap[role.teamId];
  };

  private getNavItem = (insightboard: IInsightboard): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: insightboard.id }) || navItemsFactory.withRandomColor(navItemsFactory.buildInsightboardNavItem(insightboard));
  };

  private insightboardIsFavorite = (insightboard: IInsightboard): boolean => {
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: insightboard.id });
  };

  private isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };

  private trackRecommendationHelpfulness(args: IRecommendationArgs): void {
    //Tracking Insightboards creation recommendation helpfulness.
    // Recommendation is helpful if user selects an insightboard from templates
    this.analyticsService.track(recommendation, {
      is_useful: args.isUseful,
      flavor: Flavor.PASSIVE,
      action: "creates_insightboard",
    });
  }
}
